import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Wifi, WifiOff, ShieldAlert, Pause, Play, Eye, X, Database, RefreshCw, Loader2 } from 'lucide-react';
import { useProspectSending } from '../hooks/useProspectSending';
import {
  DAYS, DEFAULT_SETTINGS, DEFAULT_TEMPLATE, MAX_BATCH_SIZE, MAX_TEMPLATE_LENGTH, MAX_VARIANTS, STATUS_LABEL, TEMPLATE_VARIABLES, VARIANT_SEPARATOR,
  buildBatch, daysToFinish, daysSinceFirstSend, effectiveDailyCap, WARMUP_STEPS, BAJAS_BRAKE, MAX_HOURLY_CAP, eligibleForBatch, previewVariants, queueSummary, queuedProspectIds, renderMessage, sentToday, timeHM, validateSettings
} from '../utils/prospectSending';

const inputClass = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green';

const STATUS_STYLE = {
  aprobado: 'text-amber-300', enviando: 'text-sky-300', enviado: 'text-emerald-300',
  error: 'text-rose-300', cancelado: 'text-seryi-text', omitido: 'text-seryi-text'
};

const shortDateTime = (iso) => (iso ? new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const daysLabel = (days) => DAYS.filter(([n]) => days.includes(n)).map(([, l]) => l).join(', ');

function ConnectionCard({ instanceName, connection, onCheck, businessName }) {
  const style = {
    connected: ['bg-emerald-500/10 text-emerald-300 border-emerald-500/30', Wifi, 'Conectado'],
    disconnected: ['bg-rose-500/10 text-rose-300 border-rose-500/30', WifiOff, 'Desconectado'],
    unknown: ['bg-seryi-bg-dark text-seryi-text border-seryi-bg-light', WifiOff, 'Sin verificar']
  }[instanceName ? connection : 'disconnected'];
  const [cls, Icon, label] = style;
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">WhatsApp desde el que se escribe</p>
          <p className="text-sm font-bold text-seryi-heading">{businessName || 'Este negocio'}{instanceName ? <span className="text-seryi-text font-mono text-[11px]"> · {instanceName}</span> : null}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px] font-bold ${cls}`}><Icon size={13} /> {label}</span>
          {instanceName && (
            <button onClick={onCheck} aria-label="Revisar conexión" className="p-2 text-seryi-text hover:text-seryi-green"><RefreshCw size={14} /></button>
          )}
        </div>
      </div>
      {!instanceName ? (
        <p className="text-xs text-amber-200">
          Este negocio todavía no tiene un WhatsApp vinculado. Escanea el código QR en{' '}
          <Link to="/v2/config" className="underline font-bold">Configuración → Identidad IA</Link> y vuelve aquí.
        </p>
      ) : connection === 'disconnected' ? (
        <p className="text-xs text-rose-200">
          El WhatsApp no está conectado. Vuelve a escanear el QR en <Link to="/v2/config" className="underline font-bold">Configuración → Identidad IA</Link>; mientras tanto no saldrá ningún mensaje.
        </p>
      ) : null}
      <div className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-2xl p-3 text-[11px] leading-relaxed">
        <ShieldAlert size={15} className="shrink-0 mt-0.5" />
        <p>
          Los mensajes salen desde el mismo número que usa tu bot. Un envío masivo puede hacer que WhatsApp lo bloquee, y eso también afectaría al bot.
          Por eso hay un tope diario (máximo 50), pausas entre mensajes y un solo mensaje por prospecto.
        </p>
      </div>
    </div>
  );
}

function SettingsForm({ initial, onSave, disabled, businessName, sample, warmup }) {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);
  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));
  const toggleDay = (n) => setForm(prev => ({
    ...prev, send_days: prev.send_days.includes(n) ? prev.send_days.filter(d => d !== n) : [...prev.send_days, n]
  }));
  const previews = previewVariants(form.message_template, sample, businessName);
  const problem = validateSettings(form);

  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-4">
      <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Mensaje y límites de envío</p>
      <div className="space-y-1">
        <label htmlFor="send-template" className="text-[10px] uppercase font-bold text-seryi-text">Mensaje</label>
        <textarea
          id="send-template" rows={9} className={inputClass} value={form.message_template} disabled={disabled}
          onChange={set('message_template')} maxLength={MAX_TEMPLATE_LENGTH + 100}
        />
        <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-seryi-text">
          <span>Variables: {TEMPLATE_VARIABLES.map(([v, d]) => <code key={v} title={d} className="font-mono text-seryi-green mr-1.5">{v}</code>)}</span>
          <span>{form.message_template.length}/{MAX_TEMPLATE_LENGTH}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-seryi-text">
        <span>
          Para variar el texto, escribe varias versiones (hasta {MAX_VARIANTS}) separadas por una línea que solo diga <code className="font-mono text-seryi-green">{VARIANT_SEPARATOR}</code>.
          Cada prospecto recibe siempre la misma, así que lo que ves al aprobar es lo que sale.
        </span>
        <button
          type="button" onClick={() => setForm(prev => ({ ...prev, message_template: DEFAULT_TEMPLATE }))} disabled={disabled}
          className="px-3 py-1.5 bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading border border-seryi-bg-light rounded-xl font-bold disabled:opacity-50"
        >
          Usar las versiones sugeridas
        </button>
      </div>
      <div className="bg-seryi-bg-dark border border-seryi-bg-light rounded-2xl p-3 space-y-3">
        <p className="text-[10px] uppercase font-bold text-seryi-text">Así lo recibe {sample.name} ({previews.length} versión{previews.length === 1 ? '' : 'es'})</p>
        {previews.map((v, i) => (
          <div key={i} className="space-y-0.5">
            {previews.length > 1 && <p className="text-[10px] font-bold text-seryi-green">Versión {i + 1}</p>}
            <p className="text-xs text-seryi-heading whitespace-pre-wrap" data-testid="template-preview">{v.text}</p>
          </div>
        ))}
        <p className="text-[10px] text-seryi-text">La línea para pedir la baja se agrega sola y no se puede quitar.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="space-y-1">
          <label htmlFor="send-cap" className="text-[10px] uppercase font-bold text-seryi-text">Máx. por día</label>
          <input id="send-cap" type="number" min="1" max="50" className={inputClass} value={form.daily_cap} disabled={disabled} onChange={set('daily_cap')} />
        </div>
        <div className="space-y-1">
          <label htmlFor="send-hourly" className="text-[10px] uppercase font-bold text-seryi-text">Máx. por hora</label>
          <input id="send-hourly" type="number" min="1" max={MAX_HOURLY_CAP} className={inputClass} value={form.hourly_cap} disabled={disabled} onChange={set('hourly_cap')} />
        </div>
        <div className="space-y-1">
          <label htmlFor="send-start" className="text-[10px] uppercase font-bold text-seryi-text">Desde</label>
          <input id="send-start" type="time" className={inputClass} value={timeHM(form.window_start)} disabled={disabled} onChange={set('window_start')} />
        </div>
        <div className="space-y-1">
          <label htmlFor="send-end" className="text-[10px] uppercase font-bold text-seryi-text">Hasta</label>
          <input id="send-end" type="time" className={inputClass} value={timeHM(form.window_end)} disabled={disabled} onChange={set('window_end')} />
        </div>
        <div className="space-y-1">
          <label htmlFor="send-min" className="text-[10px] uppercase font-bold text-seryi-text">Pausa mín. (s)</label>
          <input id="send-min" type="number" min="20" className={inputClass} value={form.min_gap_seconds} disabled={disabled} onChange={set('min_gap_seconds')} />
        </div>
        <div className="space-y-1">
          <label htmlFor="send-max" className="text-[10px] uppercase font-bold text-seryi-text">Pausa máx. (s)</label>
          <input id="send-max" type="number" min="20" max="900" className={inputClass} value={form.max_gap_seconds} disabled={disabled} onChange={set('max_gap_seconds')} />
        </div>
      </div>
      <div className="bg-sky-500/10 border border-sky-500/30 text-sky-100 rounded-2xl p-3 text-[11px] leading-relaxed space-y-1" data-testid="protections">
        <p className="font-bold flex items-center gap-1.5"><ShieldAlert size={13} /> Protecciones automáticas para que WhatsApp no bloquee tu número</p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li>
            <strong>Calentamiento:</strong> el tope diario real es menor los primeros días ({WARMUP_STEPS.slice(0, -1).map(s => `${s.cap} desde el día ${s.fromDay + 1}`).join(', ')}; después, tu tope).
            {warmup ? <> Hoy: <strong>día {warmup.day + 1}</strong>, máximo <strong>{warmup.cap}</strong> mensajes.</> : null}
          </li>
          <li><strong>Freno por bajas:</strong> si {BAJAS_BRAKE.absolute} personas piden la baja, o {BAJAS_BRAKE.minimum} o más que sean el {BAJAS_BRAKE.percentOfSent} % o más de lo enviado en 7 días, los envíos se pausan solos.</li>
          <li><strong>Siempre:</strong> pausa aleatoria entre mensajes, solo en el horario y los días elegidos, solo a números con WhatsApp confirmado, un mensaje por prospecto y pausa ante cualquier fallo.</li>
        </ul>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase font-bold text-seryi-text mr-1">Días</span>
        {DAYS.map(([n, label]) => (
          <button
            key={n} type="button" onClick={() => toggleDay(n)} disabled={disabled} aria-pressed={form.send_days.includes(n)}
            className={`px-3 py-1 rounded-xl text-[11px] font-bold transition-all ${form.send_days.includes(n) ? 'bg-seryi-green text-seryi-bg-dark' : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-rose-300 min-h-[1rem]">{problem}</p>
        <button
          onClick={() => onSave(form)} disabled={disabled || Boolean(problem)}
          className="px-4 py-2 bg-seryi-green text-seryi-bg-dark rounded-xl text-xs font-extrabold disabled:opacity-50"
        >
          Guardar ajustes
        </button>
      </div>
    </div>
  );
}

export default function ProspectSendPanel({ prospects, keys }) {
  const {
    settings, messages, loading, tableMissing, connection, instanceName, isReadOnlyDemo, businessName,
    checkConnection, saveSettings, setSending, approveBatch, cancelMessage, cancelPending
  } = useProspectSending();
  const [selected, setSelected] = useState(() => new Set());
  const [openPreview, setOpenPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const current = useMemo(() => ({ ...DEFAULT_SETTINGS, ...(settings || {}) }), [settings]);
  const saved = Boolean(settings);
  const disabled = tableMissing || isReadOnlyDemo;

  const queued = useMemo(() => queuedProspectIds(messages), [messages]);
  const eligible = useMemo(() => eligibleForBatch(prospects, keys, queued), [prospects, keys, queued]);
  const sample = eligible[0] || prospects?.[0] || { name: 'Barbería Ejemplo', category: 'barbería', city: 'tu ciudad' };
  const summary = useMemo(() => queueSummary(messages), [messages]);
  const today = sentToday(messages);
  const warmupDay = useMemo(() => daysSinceFirstSend(messages), [messages]);
  const dailyLimit = effectiveDailyCap(current.daily_cap, warmupDay);
  const warmup = { day: warmupDay, cap: dailyLimit };
  const byId = useMemo(() => new Map((prospects || []).map(p => [p.id, p])), [prospects]);

  // Si un prospecto elegido deja de ser elegible (otro lote, "No contactar"…), se quita de la selección.
  useEffect(() => {
    const ids = new Set(eligible.map(p => p.id));
    setSelected(prev => {
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [eligible]);

  const chosen = eligible.filter(p => selected.has(p.id));
  const batch = useMemo(() => buildBatch(chosen, current.message_template, businessName), [chosen, current.message_template, businessName]);
  const batchInvalid = batch.some(i => !i.ok);
  const toggle = (id) => setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else if (next.size < MAX_BATCH_SIZE) next.add(id); return next; });

  const approve = async () => {
    const days = daysToFinish(summary.aprobado + batch.length, dailyLimit);
    const ok = window.confirm(
      `Vas a aprobar ${batch.length} mensaje${batch.length === 1 ? '' : 's'}.\n\n` +
      `Saldrán desde el WhatsApp de ${businessName || 'este negocio'}, ${daysLabel(current.send_days)} entre ${timeHM(current.window_start)} y ${timeHM(current.window_end)}, ` +
      `máximo ${dailyLimit} por día${dailyLimit < current.daily_cap ? ' (calentamiento: sube solo con los días)' : ''} y ${current.hourly_cap} por hora (unos ${days} día${days === 1 ? '' : 's'}), con ${current.min_gap_seconds}–${current.max_gap_seconds} s entre mensajes.\n` +
      'Cada prospecto recibe un solo mensaje. ¿Aprobar?'
    );
    if (!ok) return;
    setBusy(true);
    const done = await approveBatch(batch);
    setBusy(false);
    if (done) { setSelected(new Set()); setOpenPreview(null); }
  };

  const sendingBlock = !saved ? 'Guarda primero los ajustes.' : connection === 'disconnected' ? 'Conecta el WhatsApp para activar los envíos.' : '';

  if (loading) return <p className="text-xs text-seryi-text py-10 text-center">Cargando envíos…</p>;

  return (
    <div className="space-y-6">
      {tableMissing && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-2xl p-4 text-xs">
          <Database size={18} className="shrink-0 mt-0.5" />
          <p>Falta crear las tablas de envío en Supabase. Ejecuta <code className="font-mono">supabase/migrations/20260930h_prospect_sending.sql</code> en el SQL Editor y recarga esta página.</p>
        </div>
      )}

      <ConnectionCard instanceName={instanceName} connection={connection} onCheck={checkConnection} businessName={businessName} />

      <SettingsForm initial={current} onSave={saveSettings} disabled={disabled} businessName={businessName} sample={sample} warmup={warmup} />

      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Envíos</p>
          <p className={`text-sm font-extrabold ${current.sending_enabled ? 'text-emerald-300' : 'text-seryi-heading'}`}>
            {current.sending_enabled ? 'Activos' : 'En pausa'} <span className="text-seryi-text font-normal text-xs">· hoy {today} de {dailyLimit}{dailyLimit < current.daily_cap ? ' (calentamiento)' : ''}</span>
          </p>
          {settings?.last_error && !current.sending_enabled && (
            <p className="text-[11px] text-rose-300 mt-1 max-w-xl">Se pausaron solos: {settings.last_error}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => setSending(!current.sending_enabled)}
            disabled={disabled || (!current.sending_enabled && Boolean(sendingBlock))}
            className={`px-4 py-2.5 rounded-2xl text-xs font-extrabold flex items-center gap-2 disabled:opacity-50 ${current.sending_enabled ? 'bg-rose-500/15 text-rose-300 border border-rose-500/30' : 'bg-seryi-green text-seryi-bg-dark'}`}
          >
            {current.sending_enabled ? <><Pause size={14} /> Pausar envíos</> : <><Play size={14} /> Activar envíos</>}
          </button>
          {!current.sending_enabled && sendingBlock && <span className="text-[10px] text-amber-300">{sendingBlock}</span>}
        </div>
      </div>

      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Preparar un lote</p>
            <p className="text-xs text-seryi-text">Solo aparecen prospectos con WhatsApp confirmado, en estado “Nuevo”, que no sean clientes y sin mensaje previo.</p>
          </div>
          <button
            onClick={() => setSelected(new Set(eligible.slice(0, Math.min(dailyLimit, MAX_BATCH_SIZE)).map(p => p.id)))}
            disabled={eligible.length === 0 || disabled}
            className="px-3 py-1.5 bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading border border-seryi-bg-light rounded-xl text-[11px] font-bold disabled:opacity-50"
          >
            Elegir los primeros {Math.min(dailyLimit, MAX_BATCH_SIZE, eligible.length)}
          </button>
        </div>

        {eligible.length === 0 ? (
          <p className="text-xs text-seryi-text py-6 text-center">No hay prospectos listos para contactar.</p>
        ) : (
          <ul className="divide-y divide-seryi-bg-light/50 text-xs">
            {eligible.map(p => {
              const msg = renderMessage(current.message_template, p, businessName);
              return (
                <li key={p.id} className="py-2.5">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" aria-label={`Elegir ${p.name}`} checked={selected.has(p.id)} onChange={() => toggle(p.id)} disabled={disabled} />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-seryi-heading truncate">{p.name}</p>
                      <p className="text-[11px] text-seryi-text font-mono">{p.phone}{p.category ? ` · ${p.category}` : ''}</p>
                    </div>
                    <button
                      onClick={() => setOpenPreview(openPreview === p.id ? null : p.id)} aria-label={`Ver mensaje para ${p.name}`}
                      className="p-2 text-seryi-text hover:text-seryi-green"
                    >
                      <Eye size={14} />
                    </button>
                  </div>
                  {openPreview === p.id && (
                    <p className="mt-2 ml-7 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-3 text-[11px] text-seryi-heading whitespace-pre-wrap">{msg.text}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <p className="text-[11px] text-seryi-text">{chosen.length} elegido{chosen.length === 1 ? '' : 's'} (máximo {MAX_BATCH_SIZE} por lote)</p>
          <button
            onClick={approve} disabled={disabled || busy || chosen.length === 0 || batchInvalid || !saved}
            className="px-4 py-2.5 bg-seryi-green text-seryi-bg-dark rounded-2xl text-xs font-extrabold flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Aprobar {chosen.length || ''} mensaje{chosen.length === 1 ? '' : 's'}
          </button>
        </div>
        {!saved && <p className="text-[11px] text-amber-300">Guarda los ajustes antes de aprobar un lote.</p>}
        {batchInvalid && <p className="text-[11px] text-rose-300">{batch.find(i => !i.ok)?.error}</p>}
      </div>

      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Cola de envío</p>
          <div className="flex flex-wrap items-center gap-3 text-[11px]">
            {['aprobado', 'enviado', 'error', 'cancelado'].map(s => (
              <span key={s} className={STATUS_STYLE[s]}>{STATUS_LABEL[s]}: <strong>{summary[s]}</strong></span>
            ))}
            {summary.aprobado > 0 && (
              <button onClick={() => window.confirm('¿Cancelar todos los mensajes que aún no salieron?') && cancelPending()} disabled={disabled}
                className="px-3 py-1 bg-rose-500/10 text-rose-300 border border-rose-500/30 rounded-lg font-bold disabled:opacity-50">
                Cancelar lo pendiente
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-seryi-text">Aprobar solo deja los mensajes en cola: los envía el flujo de n8n “Prospección – Envío”, que debe estar instalado y activo.</p>
        {messages.length === 0 ? (
          <p className="text-xs text-seryi-text py-6 text-center">Todavía no hay mensajes aprobados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[560px]">
              <thead>
                <tr className="border-b border-seryi-bg-light text-[10px] uppercase font-bold text-seryi-text tracking-wider">
                  <th className="py-2 px-3">Prospecto</th><th className="py-2 px-3">Estado</th><th className="py-2 px-3">Aprobado</th><th className="py-2 px-3">Enviado</th><th className="py-2 px-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-seryi-bg-light/50 text-xs">
                {messages.slice(0, 50).map(m => (
                  <tr key={m.id}>
                    <td className="py-2.5 px-3">
                      <p className="font-bold text-seryi-heading">{byId.get(m.prospect_id)?.name || 'Prospecto eliminado'}</p>
                      <p className="text-[11px] text-seryi-text font-mono">{m.phone}</p>
                      {m.status === 'error' && m.error && <p className="text-[11px] text-rose-300">{m.error}</p>}
                    </td>
                    <td className={`py-2.5 px-3 font-bold ${STATUS_STYLE[m.status] || ''}`}>{STATUS_LABEL[m.status] || m.status}</td>
                    <td className="py-2.5 px-3 text-seryi-text">{shortDateTime(m.approved_at)}</td>
                    <td className="py-2.5 px-3 text-seryi-text">{shortDateTime(m.sent_at)}</td>
                    <td className="py-2.5 px-3 text-right">
                      {m.status === 'aprobado' && (
                        <button onClick={() => cancelMessage(m.id)} disabled={disabled} aria-label={`Cancelar mensaje a ${byId.get(m.prospect_id)?.name || m.phone}`} className="p-1.5 text-seryi-text hover:text-rose-400">
                          <X size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
